import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFlyoutDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetFlyoutDrawerIsOpen";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrencies,
    getFulfillmentLabel,
    getIsPunchOutSession,
    getLanguages,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setCurrency from "@insite/client-framework/Store/Context/Handlers/SetCurrency";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import removeCart from "@insite/client-framework/Store/Data/Carts/Handlers/RemoveCart";
import { getHeader, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByPageType, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ShowInventoryToggle, { ShowInventoryToggleStyles } from "@insite/content-library/Components/ShowInventoryToggle";
import ShowPricingToggle, { ShowPricingToggleStyles } from "@insite/content-library/Components/ShowPricingToggle";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Globe from "@insite/mobius/Icons/Globe";
import MapPin from "@insite/mobius/Icons/MapPin";
import Menu from "@insite/mobius/Icons/Menu";
import { MappedLink } from "@insite/mobius/Menu";
import PanelMenu, { PanelMenuPresentationProps } from "@insite/mobius/PanelMenu";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

/* Store Custom Code - Start */
import User from "@store/Components/Icons/User";
import breakpointValuesMediaQueries from "@store/Framework/Common/Utilities/breakpointValuesMediaQueries";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import { TYPOGRAPHY } from "@store/theme/common/design";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { Session } from "@insite/client-framework/Services/SessionService";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
/* Store Custom Code - End */

const mapStateToProps = (state: ApplicationState) => {
    const headerLinkListLinkFields =
        (getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Header/HeaderLinkList";
        })?.fields.links as LinkModel[]) ?? [];
    const displayPricingAndInventory = state.context.session?.displayPricingAndInventory || false;
    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";

    /* Store Custom Code - Start */
    const currentCart = getCurrentCartState(state).value;
    /* Store Custom Code - End */

    return {
        currencies: getCurrencies(state),
        currentCurrencyId: state.context.session.currency?.id,
        currentCurrencySymbol: state.context.session.currency?.currencySymbol,
        languages: getLanguages(state),
        currentLanguage: state.context.session.language,
        currentLocation: getLocation(state),
        userName: state.context.session?.userName,
        isGuest: state.context.session?.isGuest,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage"),
        vmiDashboardPageUrl: getPageLinkByPageType(state, "VmiDashboardPage")?.url,
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
        headerLinkListLinkFields,
        showCustomerMenuItem: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        fulfillmentLabel: getFulfillmentLabel(state),
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        flyoutDrawerIsOpen: state.components.addressDrawer.flyoutDrawerIsOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        cart: getCurrentCartState(state).value,
        visibilityState: findVisibilityState(state.data.pages.widgetsById),
        displayPricingAndInventoryToggles: false, // Store Custom Code
        // Store Custom Code
        userLabel: state.context.session.userLabel,
        currentBillToValue: getBillToState(state, currentCart?.billToId)?.value,
        currentShipToValue: getShipToState(state, currentCart?.shipToId)?.value,
        changeCustomerLink: getPageLinkByPageType(state, "ChangeCustomerPage"),
        canSeeChangeCustomer: state.context.session.displayChangeCustomerLink,
    };
};

const mapDispatchToProps = {
    setCurrency,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    removeCart,
    setSignInOverlayIsOpen,
};

interface OwnProps {
    links: MappedLink[];
    showQuickOrder: boolean;
    quickOrderLink: PageLinkModel | undefined;
    displayVmiNavigation?: boolean;
    vmiPageLinks?: (PageLinkModel | undefined)[];
    displayModeSwitch?: boolean;
    currentMode?: string;
    isCompactHeaderFlyout?: boolean;
    showFlyoutPricingToggle?: boolean;
    showFlyoutInventoryToggle?: boolean;
    rootElementSelector?: string;
    allowOpenParentLinks?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

export interface NavigationDrawerStyles {
    menuTriggerButton?: ButtonPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerSectionWrapper?: InjectableCss;
    currencySelectorWrapper?: InjectableCss;
    languageSelectorWrapper?: InjectableCss;
    panelMenu?: PanelMenuPresentationProps;
    panelSectionWrapper?: InjectableCss;
    menuRowIcon?: IconProps;
    userRowTypography?: TypographyPresentationProps;
    punchOutUserRowText?: TypographyPresentationProps;
    mainNavigationRow?: PanelRowPresentationProps;
    mainNavigationRowIcon?: IconProps;
    punchOutNavigationRowIcon?: IconProps;
    mainNavigationRowTypography?: TypographyPresentationProps;
    logoLinks?: PanelRowPresentationProps & { typographyProps: TypographyPresentationProps };
    currencySymbol?: TypographyPresentationProps;
    changeCustomerRow?: PanelRowPresentationProps;
    changeCustomerRowContainer?: GridContainerProps;
    fulfillmentMethodGridItem?: GridItemProps;
    addressesGridItem?: GridItemProps;
    pickUpAddressGridItem?: GridItemProps;
    applyButtonGridItem?: GridItemProps;
    showInventoryToggle?: ShowInventoryToggleStyles;
    showPricingToggle?: ShowPricingToggleStyles;
    signOutRow?: PanelRowPresentationProps;
    signOutRowText?: TypographyPresentationProps;
    // Store Custom Code
    intoTypography?: TypographyPresentationProps;
    signInRow?: PanelRowPresentationProps;
    divider?: InjectableCss;
    selectorPanelMenu?: PanelMenuPresentationProps;
    signedInPanelSectionWrapper?: InjectableCss;
    customerInformationWrapper?: InjectableCss;
    customerTopWrapper?: InjectableCss;
    currentBillToGridItem?: InjectableCss;
    shipToBillToTypography?: TypographyPresentationProps;
    linkGridItem?: InjectableCss;
    linkStyle?: LinkPresentationProps;
    currentShipToGridItem?: InjectableCss;
}

const StyledSection = getStyledWrapper("section");
const StyledSpan = getStyledWrapper("span");

export const navigationDrawerStyles: NavigationDrawerStyles = {
    menuTriggerButton: {
        shape: "pill",
        color: "secondary",
        size: 50,
        css: css`
            &:focus {
                outline: none;
            }

            /* Store Custom Code */
            padding: 0 10px;
            border: 0;

            &:hover {
                background: 0;
                border: 0;
            }

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["0", null, null, "10px", null], properties: ["margin-right"] }],
                    mediaOptions.min,
                )}
        `,
    },
    drawer: {
        size: 300,
        cssOverrides: {
            drawerContent: css`
                margin-top: -2px;
                overflow-x: hidden;
            `,
            // Store Custom Code
            drawerTitle: css`
                background: ${getColor("common.background")};
            `,
            drawerBody: css`
                background: ${getColor("common.background")};
                padding: 0 10px 0 30px;

                &::-webkit-scrollbar {
                    display: none;
                }
            `,
            titleButton: css`
                min-width: 0;
                padding: 5px;
                border: 0;
                background: 0;
                min-height: 25px;
                border-radius: 2px;

                svg {
                    color: ${getColor("secondary")};
                }

                &:hover,
                &:focus {
                    border: 0;

                    svg {
                        color: ${getColor("common.background")};
                    }
                }
            `,
        },
    },
    drawerSectionWrapper: {
        css: css`
            &:first-child {
                margin-top: 0;

                /* Store Removed Code - padding-bottom */

                /* Store Removed Code - background */
            }
            &:last-child {
                margin-bottom: 5px;
            }
            margin-top: 20px;
        `,
    },
    panelMenu: {
        cssOverrides: {
            wrapper: css`
                text-align: left;
                > a,
                button {
                    height: 42px;
                }
            `,
        },
        // Store Custom Code
        panelRowProps: {
            css: css`
                width: 100%;

                &:hover,
                &:focus {
                    span {
                        text-decoration: underline;
                    }
                }
            `,
        },
    },
    panelSectionWrapper: {
        css: css`
            height: 22px;
            display: flex;
            align-items: center;
        `,
    },
    menuRowIcon: {
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    userRowTypography: {
        /* Store Removed Code - transform */
        /* Store Custom Code - Start */
        color: "secondary",
        weight: 500,
        size: 16,
        /* Store Custom Code - End */
    },
    punchOutUserRowText: {
        transform: "uppercase",
        color: "secondary", // Store Custom Code
    },
    mainNavigationRow: {
        color: "common.background", // Store Custom Code
        css: css`
            width: 100%;
            margin: 2px;
            padding: 15px 0; /* Store Custom Code */

            /* Store Custom Code - Start */
            &:hover,
            &:focus {
                span {
                    text-decoration: underline;
                }
            }

            /* Store Custom Code - End */
        `,
    },
    mainNavigationRowIcon: {
        /* Store Custom Code - Start */
        src: User,
        color: "secondary",
        size: 20,
        css: css`
            justify-content: center;
            border-radius: 50%;
            width: 45px;
            height: 45px;
            background-color: ${getColor("text.almostDark")};
            border: 2px solid ${getColor("text.almostDark")};
            overflow: hidden;

            svg {
                width: 35px;
                height: 35px;
                margin-top: 10px;
                color: ${getColor("common.background")};
            }

            ${({ theme }) => {
                return `
                    ${breakpointValuesMediaQueries(
                        theme,
                        [
                            {
                                values: ["10px", null, null, "15px", null],
                                properties: ["margin-right"],
                            },
                        ],
                        mediaOptions.min,
                    )}`;
            }}
        `,
        /* Store Custom Code - End */
    },
    punchOutNavigationRowIcon: {
        src: "User",
        color: "secondary", // Store Custom Code
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    mainNavigationRowTypography: {
        transform: "uppercase",
        color: "secondary", // Store Custom Code
        weight: "bold",
        // Store Custom Code
        size: 16,
        lineHeight: TYPOGRAPHY.lineHeight.small,
    },
    logoLinks: {
        color: "common.background", // Store Custom Code
        typographyProps: {
            ellipsis: true,
            /* Store Removed Code - transform */
            // Store Custom Code
            weight: 500,
            size: 16,
            color: "text.almostDark",
        },
        css: css`
            margin: 2px;
            padding: 10px 0; /* Store Custom Code */

            /* Store Custom Code - Start */
            &:hover,
            &:focus {
                span {
                    text-decoration: underline;
                }
            }

            /* Store Custom Code - End */
        `,
    },
    currencySymbol: {
        size: 22,
        css: css`
            margin-top: -4px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    changeCustomerRow: {
        css: css`
            &:focus {
                outline: none;
            }
        `,
    },
    changeCustomerRowContainer: { gap: 15 },
    fulfillmentMethodGridItem: { width: 12 },
    addressesGridItem: { width: 12 },
    pickUpAddressGridItem: { width: 12 },
    applyButtonGridItem: { width: 12 },
    showInventoryToggle: {
        toggleClickable: {
            css: css`
                text-transform: uppercase;
                padding: 10px 10px 10px 20px;
            `,
        },
    },
    showPricingToggle: {
        toggleClickable: {
            css: css`
                text-transform: uppercase;
                padding: 10px 10px 10px 20px;
            `,
        },
    },
    signOutRowText: {
        /* Store Removed Code - transform */
        // Store Custom Code
        ellipsis: true,
        color: "primary.main",
        size: 16,
        weight: 500,
    },
    // Store Custom Code
    signedInPanelSectionWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    intoTypography: {
        size: 16,
        color: "text.main",
        weight: 500,
    },
    signOutRow: {
        css: css`
            padding: 10px 0;
            background: 0;
            margin: 2px;

            &:hover,
            &:focus {
                span {
                    text-decoration: underline;
                }
            }
        `,
    },
    signInRow: {
        color: "common.background",
        css: css`
            width: 100%;
            margin: 15px 2px 2px;
            padding: 20px 0;

            &:hover,
            &:focus {
                span {
                    text-decoration: underline;
                }
            }
        `,
    },
    divider: {
        css: css`
            min-width: 100%;
            margin: 15px 0;
            border: none;
            height: 1px;
            background-color: ${getColor("common.borderLighter")};
        `,
    },
    selectorPanelMenu: {
        bodyColor: "common.background",
        panelRowProps: {
            color: "common.background",
            css: css`
                width: 100%;

                &:hover,
                &:focus {
                    span {
                        text-decoration: underline;
                    }
                }
            `,
        },
    },
    customerInformationWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    customerTopWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
        `,
    },
    currentBillToGridItem: {
        css: css`
            word-break: break-word;
            font-size: ${TYPOGRAPHY.size.base};
        `,
    },
    linkStyle: {
        css: css`
            width: auto !important;

            &:hover,
            &:focus {
                background: transparent !important;
            }
        `,
        typographyProps: {
            weight: TYPOGRAPHY.weight.bold,
            size: TYPOGRAPHY.size.small,
        },
    },
    shipToBillToTypography: {
        weight: TYPOGRAPHY.weight.bold,
        color: "text.almostDark",
        size: TYPOGRAPHY.size.base,
        css: css`
            white-space: nowrap;
        `,
    },
};

const styles = navigationDrawerStyles;

/* Store Custom Code - Start */
const Divider = styled.hr`
    ${styles.divider?.css}
`;
/* Store Custom Code - End */

const NavigationDrawer = ({
    links,
    showQuickOrder,
    quickOrderLink,
    displayVmiNavigation,
    vmiPageLinks,
    displayModeSwitch,
    currentMode,
    isCompactHeaderFlyout,
    showFlyoutPricingToggle,
    showFlyoutInventoryToggle,
    rootElementSelector,
    currencies,
    currentCurrencyId,
    currentCurrencySymbol,
    languages,
    currentLanguage,
    currentLocation,
    userName,
    isGuest,
    myAccountPageLink,
    signInUrl,
    vmiDashboardPageUrl,
    homePageUrl,
    headerLinkListLinkFields,
    showCustomerMenuItem,
    fulfillmentLabel,
    navDrawerIsOpen,
    flyoutDrawerIsOpen,
    isPunchOutSession,
    cart,
    history,
    theme,
    displayPricingAndInventoryToggles,
    setCurrency,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    removeCart,
    setSignInOverlayIsOpen,
    visibilityState,
    allowOpenParentLinks,
    // Store Custom Code
    userLabel,
    currentBillToValue,
    currentShipToValue,
    changeCustomerLink,
    canSeeChangeCustomer,
}: Props) => {
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    const { links: headerLinkListLinks, hasMissingCategories } = useGetLinks(
        headerLinkListLinkFields,
        o => o.fields.destination,
    );

    if (hasMissingCategories) {
        return null;
    }
    const openDrawer = () => {
        isCompactHeaderFlyout ? setFlyoutDrawerIsOpen({ isOpen: true }) : setNavDrawerIsOpen({ navDrawerIsOpen: true });
        setInitialValues({});
    };

    const closeDrawer = () => {
        if (isCompactHeaderFlyout) {
            setFlyoutDrawerIsOpen({ isOpen: false });
            setTimeout(() => setFlyoutDrawerIsOpen({ isOpen: undefined }), 300);
            return;
        }

        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    const signInPanelClickHandler = (event: React.MouseEvent) => {
        if (theme.signIn?.useOverlay) {
            event.preventDefault();
            setSignInOverlayIsOpen({ isOpen: true });
        }

        closeDrawer();
    };

    const signInPanelKeyPressHandler = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
        if (event.key === " " && history && signInUrl?.url) {
            event.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
            } else {
                history.push(signInUrl.url);
            }

            closeDrawer();
        }
    };

    const onSignOutHandler = () => {
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    const navigationModeChangeHandler = (navigationMode: "Storefront" | "Vmi") => {
        setCookie(NavigationModeCookieName, navigationMode);
        if (navigationMode === "Vmi") {
            vmiDashboardPageUrl && history.push(vmiDashboardPageUrl);
        } else if (cart?.status === "AlternateCart") {
            removeCart({
                cartId: API_URL_CURRENT_FRAGMENT,
                isAlternateCart: true,
                onSuccess: () => {
                    homePageUrl && history.push(homePageUrl);
                },
            });
        } else {
            homePageUrl && history.push(homePageUrl);
        }
    };

    const currentPageUrl = currentLocation.pathname;
    const isOpen = isCompactHeaderFlyout ? flyoutDrawerIsOpen : navDrawerIsOpen;

    /* Store Custom Code - Start */
    let menuItems = [
        ...(myAccountPageLink?.children?.filter(
            child =>
                child.type !== "OrderApprovalListPage" &&
                child.type !== "BudgetManagementPage" &&
                child.type !== "RequisitionsPage" &&
                child.type !== "AddressesPage" &&
                child.type !== "InvoiceHistoryPage",
        ) ?? []),
    ];

    if (myAccountPageLink) {
        menuItems = [
            {
                id: "",
                title: myAccountPageLink.title,
                url: myAccountPageLink.url,
            },
            ...menuItems,
        ];
    }

    if (quickOrderLink) {
        menuItems = [
            ...menuItems,
            {
                id: "",
                title: quickOrderLink.title,
                url: quickOrderLink.url,
            },
        ];
    }

    let userDisplayName = userName;
    if (userLabel) {
        userDisplayName = userLabel;
    }
    /* Store Custom Code - End */

    return (
        <>
            <Button
                className="menu-trigger-button"
                onClick={openDrawer}
                {...styles.menuTriggerButton}
                data-test-selector="expandMobileMenu"
            >
                <ButtonIcon className="menu-trigger-button-icon" src={Menu} />
                <VisuallyHidden>{translate("menu")}</VisuallyHidden>
            </Button>
            <Drawer
                className="drawer"
                draggable
                position="left"
                {...styles.drawer}
                isOpen={isOpen}
                handleClose={closeDrawer}
                contentLabel="menu drawer"
                data-test-selector="drawerSection"
                rootElementSelector={rootElementSelector}
            >
                <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                    {userDisplayName /* Store Custom Code */ && !isGuest && !isPunchOutSession ? (
                        <PanelMenu
                            className="panel-menu"
                            /* Store Removed Code - currentUrl */
                            panelTrigger={
                                <PanelRow
                                    className="main-navigation-row"
                                    hasChildren
                                    {...styles.signInRow} /* Store Custom Code */
                                >
                                    <StyledSpan
                                        className="panel-section-wrapper"
                                        {...styles.signedInPanelSectionWrapper} /* Store Custom Code */
                                    >
                                        {/* Store Removed Code - Icon */}
                                        {/* Store Custom Code - Start */}
                                        <Typography {...styles.intoTypography}>{translate("Welcome")}</Typography>
                                        {/* Store Custom Code - End */}
                                        <Typography className="user-row-text" {...styles.userRowTypography}>
                                            {userDisplayName /* Store Custom Code */}
                                        </Typography>
                                    </StyledSpan>
                                </PanelRow>
                            }
                            menuItems={menuItems} // Store Custom Code
                            maxDepth={3}
                            closeOverlay={closeDrawer}
                            layer={0}
                            {...styles.panelMenu}
                        />
                    ) : isPunchOutSession ? (
                        <PanelRow className="main-navigation-row" {...styles.mainNavigationRow}>
                            <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                                <Icon className="punch-out-navigation-row-icon" {...styles.punchOutNavigationRowIcon} />
                                <Typography className="punch-out-user-row-text" {...styles.punchOutUserRowText}>
                                    {userDisplayName /* Store Custom Code */}
                                </Typography>
                            </StyledSpan>
                        </PanelRow>
                    ) : (
                        <PanelRow
                            className="main-navigation-row"
                            {...styles.signInRow} // Store Custom Code
                            /* Store Removed Code - isCurrent */
                            onClick={signInPanelClickHandler}
                            onKeyUp={signInPanelKeyPressHandler}
                            href={signInUrl?.url}
                        >
                            <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                                {showIcon && (
                                    <Icon className="main-navigation-row-icon" {...styles.mainNavigationRowIcon} />
                                )}
                                {showLabel && (
                                    <Typography className="user-row-text" {...styles.userRowTypography}>
                                        {translate("Sign In")}
                                    </Typography>
                                )}
                            </StyledSpan>
                        </PanelRow>
                    )}
                    {/* Store Custom Code - Start */}
                    <Divider />
                    {userName && !isGuest && !isPunchOutSession && (
                        <>
                            <StyledWrapper {...styles.customerInformationWrapper}>
                                <StyledWrapper {...styles.customerTopWrapper}>
                                    <StyledWrapper {...styles.currentBillToGridItem}>
                                        <Typography {...styles.shipToBillToTypography}>
                                            {translate("Customer #")}:{" "}
                                        </Typography>
                                        {currentBillToValue?.customerNumber}
                                    </StyledWrapper>
                                    {canSeeChangeCustomer && (
                                        <StyledWrapper {...styles.linkGridItem}>
                                            <Link
                                                href={changeCustomerLink?.url}
                                                onClick={closeDrawer}
                                                {...styles.linkStyle}
                                            >
                                                {translate("Change")}
                                            </Link>
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                                <StyledWrapper {...styles.currentShipToGridItem}>
                                    <Typography {...styles.shipToBillToTypography}>
                                        {translate("accountMenuShipTo")}:{" "}
                                    </Typography>
                                    {currentShipToValue?.companyName
                                        ? currentShipToValue?.companyName
                                        : currentShipToValue?.customerSequence}
                                    {currentShipToValue?.address1 && `, ${currentShipToValue?.fullAddress}`}
                                </StyledWrapper>
                            </StyledWrapper>
                            <Divider />
                        </>
                    )}
                    {/* Store Custom Code - End */}
                    {/* covers MainNavigation functionality */}
                    {!displayVmiNavigation &&
                        links.map((link, index) => {
                            if (
                                !!link.childrenType &&
                                link.children &&
                                link.children.filter(o => !o.excludeFromNavigation).length > 0
                            ) {
                                const href =
                                    allowOpenParentLinks && link.url && link.url !== currentPageUrl
                                        ? link.url
                                        : undefined;

                                return (
                                    <PanelMenu
                                        className="panel-menu"
                                        key={index}
                                        /* Store Removed Code - currentUrl */
                                        closeOverlay={closeDrawer}
                                        panelTrigger={
                                            <PanelRow
                                                className="main-navigation-row"
                                                href={href}
                                                isCurrent={link.url === currentPageUrl}
                                                hasChildren={true}
                                                allowOpenParentLinks={allowOpenParentLinks}
                                                {...styles.mainNavigationRow}
                                            >
                                                <Typography
                                                    className="main-navigation-row-text"
                                                    {...styles.mainNavigationRowTypography}
                                                >
                                                    {link.title}
                                                </Typography>
                                            </PanelRow>
                                        }
                                        menuItems={link.children}
                                        maxDepth={link.maxDepth || 3}
                                        layer={0}
                                        allowOpenParentLinks={allowOpenParentLinks}
                                        {...styles.panelMenu}
                                    />
                                );
                            }
                            return (
                                <PanelRow
                                    key={index}
                                    href={link.url}
                                    target={link.openInNewWindow ? "_blank" : ""}
                                    className="main-navigation-row"
                                    onClick={closeDrawer}
                                    {...styles.mainNavigationRow}
                                >
                                    <Typography
                                        className="main-navigation-row-text"
                                        {...styles.mainNavigationRowTypography}
                                    >
                                        {link.title}
                                    </Typography>
                                </PanelRow>
                            );
                        })}
                    {!displayVmiNavigation && showQuickOrder && quickOrderLink && (
                        <PanelRow
                            className="main-navigation-row"
                            isCurrent={currentPageUrl === quickOrderLink.url}
                            onClick={closeDrawer}
                            href={quickOrderLink.url}
                            {...styles.mainNavigationRow}
                        >
                            <Typography className="main-navigation-row-text" {...styles.mainNavigationRowTypography}>
                                {quickOrderLink.title}
                            </Typography>
                        </PanelRow>
                    )}
                    {displayVmiNavigation &&
                        vmiPageLinks &&
                        vmiPageLinks.map(
                            link =>
                                link && (
                                    <PanelRow
                                        className="main-navigation-row"
                                        key={link.id}
                                        isCurrent={currentPageUrl === link.url}
                                        onClick={closeDrawer}
                                        href={link.url}
                                        {...styles.mainNavigationRow}
                                    >
                                        <Typography
                                            className="main-navigation-row-text"
                                            {...styles.mainNavigationRowTypography}
                                        >
                                            {link.title}
                                        </Typography>
                                    </PanelRow>
                                ),
                        )}
                </StyledSection>
                {/* Store Custom Code - Start */}
                <Divider />
                {/* Store Custom Code - End */}
                {headerLinkListLinks.length > 0 && (
                    <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                        {/* Store Custom Code - Start */}
                        {headerLinkListLinks.map((link, index) => {
                            const linkFields = headerLinkListLinkFields[index].fields;
                            const title =
                                linkFields.destination.type === "Url" &&
                                "title" in linkFields &&
                                typeof linkFields.title === "string" &&
                                linkFields.title !== ""
                                    ? linkFields.title
                                    : link.title;

                            return (
                                <PanelRow
                                    className="logo-links-row"
                                    key={link.title + String(index)}
                                    {...(styles.logoLinks && omitSingle(styles.logoLinks, "typographyProps"))}
                                    /* Store Removed Code - isCurrent */
                                    onClick={closeDrawer}
                                    onKeyUp={(event: React.KeyboardEvent<HTMLAnchorElement>) => {
                                        if (event.key === " " && history && link?.url) {
                                            event.preventDefault();
                                            closeDrawer();
                                            history.push(link.url);
                                        }
                                    }}
                                    href={link.url}
                                    target={headerLinkListLinkFields[index].fields.openInNewWindow ? "_blank" : ""}
                                >
                                    <Typography className="logo-link-title" {...styles.logoLinks?.typographyProps}>
                                        {title}
                                    </Typography>
                                </PanelRow>
                            );
                        })}
                        {userName && !isGuest && (
                            <PanelRow className="sign-out-row" onClick={onSignOutHandler} {...styles.signOutRow}>
                                <Typography className="sign-out-row-text" {...styles.signOutRowText}>
                                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                                </Typography>
                            </PanelRow>
                        )}
                        {/* Store Custom Code - End */}
                    </StyledSection>
                )}
                <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                    <StyledWrapper className="currency-selector-wrapper" {...styles.currencySelectorWrapper}>
                        <SelectorMenu
                            options={currencies?.map(c => {
                                return {
                                    title: c.currencyCode.toUpperCase(),
                                    clickableProps: {
                                        onClick: () => {
                                            setCurrency({ currencyId: c.id });
                                            closeDrawer();
                                        },
                                    },
                                };
                            })}
                            closeModal={closeDrawer}
                            currentOption={currencies?.find(c => c.id === currentCurrencyId)?.currencyCode}
                            currentOptionIcon={
                                <Typography {...styles.currencySymbol}>{currentCurrencySymbol}</Typography>
                            }
                        />
                    </StyledWrapper>
                    <StyledWrapper className="language-selector-wrapper" {...styles.languageSelectorWrapper}>
                        <SelectorMenu
                            dataTestSelector="mobileLanguageSelector"
                            options={languages?.map(l => {
                                return {
                                    title: l.languageCode.toUpperCase(),
                                    clickableProps: {
                                        onClick: () => {
                                            setLanguage({ languageId: l.id });
                                            closeDrawer();
                                        },
                                    },
                                };
                            })}
                            currentOption={currentLanguage?.languageCode.toUpperCase()} // Store Custom Code
                            closeModal={closeDrawer}
                            currentOptionIcon={
                                currentLanguage?.imageFilePath ? (
                                    <LogoImage className="language-image" src={currentLanguage.imageFilePath} alt="" />
                                ) : (
                                    <Icon className="menu-row-icon" src={Globe} {...styles.menuRowIcon} />
                                )
                            }
                        />
                    </StyledWrapper>
                    {showCustomerMenuItem && (
                        <PanelMenu
                            className="panel-menu"
                            {...styles.panelMenu}
                            panelTrigger={
                                <PanelRow
                                    hasChildren
                                    className="logo-links-row"
                                    {...styles.logoLinks}
                                    color="common.accent"
                                >
                                    <Typography className="logo-link-title" {...styles.logoLinks?.typographyProps}>
                                        <Icon src={MapPin} className="menu-row-icon" {...styles.menuRowIcon} />
                                        {fulfillmentLabel}
                                    </Typography>
                                </PanelRow>
                            }
                            layer={0}
                            closeOverlay={closeDrawer}
                        >
                            {isPunchOutSession ? (
                                <PanelRow tabIndex={-1} className="change-customer-row" {...styles.changeCustomerRow}>
                                    {createWidgetElement("Header/AddressDrawerPunchOutCustomer", {
                                        fields: {},
                                    })}
                                </PanelRow>
                            ) : (
                                <PanelRow tabIndex={-1} className="change-customer-row" {...styles.changeCustomerRow}>
                                    <GridContainer
                                        className="change-customer-row-container"
                                        {...styles.changeCustomerRowContainer}
                                    >
                                        <GridItem
                                            className="fulfillment-method-grid-item"
                                            {...styles.fulfillmentMethodGridItem}
                                        >
                                            {createWidgetElement("Header/AddressDrawerFulfillmentMethodSelector", {
                                                fields: {},
                                            })}
                                        </GridItem>
                                        <GridItem className="addresses-grid-item" {...styles.addressesGridItem}>
                                            {createWidgetElement("Header/AddressDrawerSelectCustomer", {
                                                fields: {},
                                            })}
                                        </GridItem>
                                        <GridItem
                                            className="pick-up-address-grid-item"
                                            {...styles.pickUpAddressGridItem}
                                        >
                                            {createWidgetElement("Header/AddressDrawerPickUpLocationSelector", {
                                                fields: {},
                                            })}
                                        </GridItem>
                                        <GridItem className="apply-button-grid-item" {...styles.applyButtonGridItem}>
                                            {createWidgetElement("Header/AddressDrawerApplyButton", { fields: {} })}
                                        </GridItem>
                                    </GridContainer>
                                </PanelRow>
                            )}
                        </PanelMenu>
                    )}
                    {displayModeSwitch && (
                        <SelectorMenu
                            options={[
                                {
                                    title: translate("Storefront"),
                                    clickableProps: {
                                        onClick: () => {
                                            navigationModeChangeHandler("Storefront");
                                            closeDrawer();
                                        },
                                    },
                                },
                                {
                                    title: translate("Vendor Managed Inventory"),
                                    clickableProps: {
                                        onClick: () => {
                                            navigationModeChangeHandler("Vmi");
                                            closeDrawer();
                                        },
                                    },
                                },
                            ]}
                            closeModal={closeDrawer}
                            currentOption={
                                currentMode === "Vmi" ? translate("Vendor Managed Inventory") : translate("Storefront")
                            }
                        />
                    )}
                    {displayPricingAndInventoryToggles && (
                        <>
                            {(!isCompactHeaderFlyout || showFlyoutInventoryToggle) && (
                                <ShowInventoryToggle extendedStyles={styles.showInventoryToggle} />
                            )}
                            {(!isCompactHeaderFlyout || showFlyoutPricingToggle) && (
                                <ShowPricingToggle extendedStyles={styles.showPricingToggle} />
                            )}
                        </>
                    )}
                    {/* Store Moved Code - signOut */}
                </StyledSection>
            </Drawer>
        </>
    );
};

const LogoImage = styled.img`
    margin-right: 10px;
    height: 22px;
`;

interface SelectorMenuProps {
    options?: MappedLink[];
    closeModal?: () => void;
    currentOption?: string;
    currentOptionIcon?: React.ReactNode;
    dataTestSelector?: string;
}

const SelectorMenu: React.FC<SelectorMenuProps> = ({
    options,
    closeModal,
    currentOption,
    currentOptionIcon,
    dataTestSelector,
}) => {
    if (!options || options.length <= 1) {
        return null;
    }

    const trigger = (
        <PanelRow hasChildren className="logo-links-row" {...styles.logoLinks}>
            <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                {currentOptionIcon}
                <Typography
                    className="logo-link-title"
                    data-test-selector={`${dataTestSelector}_currentOption`}
                    {...styles.logoLinks?.typographyProps}
                >
                    {currentOption}
                </Typography>
            </StyledSpan>
        </PanelRow>
    );

    return (
        <PanelMenu
            className="panel-menu"
            currentUrl={currentOption}
            data-test-selector={dataTestSelector}
            closeOverlay={closeModal}
            panelTrigger={trigger}
            menuItems={options}
            maxDepth={1}
            layer={0}
            {...styles.selectorPanelMenu} // Store Custom Code
        />
    );
};

function findVisibilityState(data: any): string | undefined {
    for (const key in data) {
        if (data[key].type === "Common/HeaderSignIn") {
            return data[key].generalFields.visibilityState;
        }
    }
    return undefined;
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(NavigationDrawer)));
